<template>
  <v-layout row justify-center align-center>
    <v-flex xs12>
      <v-card>
        <errors-list :errorMessage="error.errorMessage" />
        <v-form ref="form" @submit.prevent="onSubmit" color="green">
          <v-card-text>
            <v-select
              :label="$t('@campaigns/typeLabel')"
              v-model="campaign.type"
              :items="[
                { text: $t('@campaignsList/type/winback'), value: 'winback' },
                { text: $t('@campaignsList/type/signup'), value: 'signup' },
              ]"
              :disabled="submitting || campaign.hasStarted || campaign.hasEnded"
            ></v-select>

            <v-text-field
              v-model="campaign.name"
              color="green darken-1"
              :label="$t('@campaigns/nameLabel')"
              name="name"
              :disabled="submitting || campaign.hasEnded"
              :error="!campaign.name"
              :error-messages="validationError(error.errorField, errorFieldMap.name)"
            />

            <v-text-field
              v-model="campaign.displayName"
              color="green darken-1"
              :label="$t('@campaigns/displayNameLabel')"
              name="displayName"
              :disabled="submitting || campaign.hasEnded"
              :error="!campaign.displayName"
              :error-messages="validationError(error.errorField, errorFieldMap.displayName)"
            />

            <v-text-field
              v-model="campaign.description"
              color="green darken-1"
              :label="$t('@campaigns/descriptionLabel')"
              name="description"
              :disabled="submitting || campaign.hasEnded"
              :error="!campaign.description"
              :error-messages="validationError(error.errorField, errorFieldMap.description)"
            />

            <v-text-field
              v-model="campaign.caption"
              color="green darken-1"
              :label="$t('@campaigns/captionLabel')"
              name="caption"
              :hint="$t('@campaigns/captionHint')"
              :disabled="submitting || campaign.hasEnded"
              :error="!campaign.caption"
              :error-messages="validationError(error.errorField, errorFieldMap.caption)"
            />

            <v-text-field
              v-model="campaign.conditions"
              color="green darken-1"
              :label="$t('@campaigns/conditionsLabel')"
              name="conditions"
              :hint="$t('@campaigns/conditionsHint')"
              :disabled="submitting || campaign.hasEnded"
              :error="!campaign.conditions"
              :error-messages="validationError(error.errorField, errorFieldMap.conditions)"
            />

            <v-text-field
              v-model="campaign.availableFrom"
              type="date"
              color="green darken-1"
              :label="$t('@campaigns/availableFromLabel')"
              name="availableFrom"
              :disabled="submitting || campaign.hasStarted || campaign.hasEnded"
              :error="!campaign.availableFrom"
              :error-messages="validationError(error.errorField, errorFieldMap.availableFrom)"
            />

            <v-text-field
              v-model="campaign.availableUntil"
              type="date"
              color="green darken-1"
              :label="$t('@campaigns/availableUntilLabel')"
              name="availableUntil"
              :disabled="submitting || campaign.hasEnded"
              :error="!campaign.availableUntil"
              :error-messages="validationError(error.errorField, errorFieldMap.availableUntil)"
            />

            <v-text-field
              v-model="campaign.discountPercent"
              color="green darken-1"
              :label="$t('@campaigns/discountPercentLabel')"
              name="discountPercent"
              :disabled="submitting || campaign.hasStarted || campaign.hasEnded"
              :error="!campaign.discountPercent"
              :error-messages="validationError(error.errorField, errorFieldMap.discountPercent)"
            />

            <v-text-field
              v-model="campaign.appliesForPeriods"
              color="green darken-1"
              :label="$t('@campaigns/appliesForPeriodsLabel')"
              name="appliesForPeriods"
              :disabled="submitting || campaign.hasStarted || campaign.hasEnded"
              :error="!campaign.appliesForPeriods"
              :error-messages="validationError(error.errorField, errorFieldMap.appliesForPeriods)"
            />

            <v-checkbox
              :label="$t('@campaigns/activeLabel')"
              color="green darken-1"
              v-model="campaign.active"
              :disabled="submitting || campaign.hasEnded"
            />

            <v-layout row justify-center align-center>
              <v-flex xs12>
                <v-list subheader dense color="green darken-1">
                  <v-subheader>{{ $t('@campaigns/productIdsLabel') }}</v-subheader>
                  <v-list-tile :key="campaignProduct.id" v-for="campaignProduct in allProducts">
                    <v-list-tile-action>
                      <v-checkbox
                        :label="`${campaignProduct.name} (${campaignProduct.id})`"
                        color="green darken-1"
                        v-model="campaign.productIds"
                        :value="campaignProduct.id"
                        :disabled="submitting || campaign.hasStarted || campaign.hasEnded"
                      />
                    </v-list-tile-action>
                  </v-list-tile>
                </v-list>
              </v-flex>
            </v-layout>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-btn
              :disabled="submitting || campaign.hasStarted"
              color="red darken-1"
              flat
              @click="deleteCampaign()"
            >
              {{ $t('@form/remove') }}
            </v-btn>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="untouchedCampaign.type === 'signup' && untouchedCampaign.promoted === false"
                  :disabled="
                    submitting ||
                      campaign.hasEnded ||
                      campaign.type === 'winback' ||
                      campaign.active == false ||
                      untouchedCampaign.active == false
                  "
                  color="green darken-1"
                  flat
                  @click="promoteCampaign()"
                  :tooltip="$t('@campaigns/promoteTooltip')"
                  v-on="on"
                >
                  {{ $t('@campaigns/promote') }}
                </v-btn>
              </template>
              <span>{{ $t('@campaigns/promoteTooltip') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="campaign.type === 'signup' && campaign.promoted === true"
                  :disabled="
                    submitting ||
                      campaign.hasEnded ||
                      campaign.type === 'winback' ||
                      campaign.active == false ||
                      untouchedCampaign.active == false
                  "
                  color="red darken-1"
                  flat
                  @click="unpromoteCampaign()"
                  v-on="on"
                >
                  {{ $t('@campaigns/unpromote') }}
                </v-btn>
              </template>
              <span>{{ $t('@campaigns/unpromoteTooltip') }}</span>
            </v-tooltip>

            <v-spacer />
            <v-btn :disabled="submitting" color="red darken-1" flat @click="closeModal()">
              {{ $t('@form/cancel') }}
            </v-btn>
            <v-btn type="submit" :disabled="submitting" color="green darken-1" flat>
              {{ $t('@form/save') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
// import { phoneRegexp, phonePrefixHardcode } from '../../utils';
import moment from 'moment';
import Vue from 'vue';
import ErrorsList from '../../components/ErrorsList';
import { getErrorMessage, validationError } from '../../errorHandlers';

const errorFieldMap = {
  type: 'type',
  productIds: 'productIds',
  name: 'name',
  displayName: 'displayName',
  description: 'description',
  caption: 'caption',
  conditions: 'conditions',
  active: 'active',
  promoted: 'promoted',
  availableFrom: 'availableFrom',
  availableUntil: 'availableUntil',
  discountPercent: 'discountPercent',
  appliesForPeriods: 'appliesForPeriods',
  hasStarted: 'hasStarted',
  hasEnded: 'hasEnded',
};

export default {
  components: { ErrorsList },
  props: {
    campaignId: {
      type: Number,
    },
  },

  data() {
    return {
      submitting: false,
      error: {
        errorMessage: '',
        errorField: '',
      },
      untouchedCampaign: {},
      campaign: {
        type: '',
        productIds: [],
        name: '',
        displayName: '',
        description: '',
        active: '',
        promoted: '',
        availableFrom: '',
        availableUntil: '',
        discountPercent: '',
        appliesForPeriods: '',
        hasStarted: '',
        hasEnded: '',
      },
      allProducts: [],
      validationError,
      errorFieldMap,
    };
  },

  async mounted() {
    await this.fetchProducts();

    if (this.campaignId) {
      await this.fetchCampaign();
    } else {
      Vue.set(this, 'campaign', {
        type: 'winback',
        productIds: [],
        name: '',
        displayName: '',
        description: '',
        active: false,
        promoted: false,
        availableFrom: moment().format('YYYY-MM-DD'),
        // eslint-disable-next-line
        availableUntil: moment().add(7, 'day').format('YYYY-MM-DD'),
        discountPercent: 30,
        appliesForPeriods: 3,
        hasStarted: false,
        hasEnded: false,
      });
    }
  },

  methods: {
    closeModal(isSent = false) {
      this.$emit('close-modal', isSent);
    },

    async deleteCampaign() {
      this.submitting = true;

      const confirm = window.confirm(this.$t('@form/confirmDelete'));

      if (!confirm) {
        this.submitting = false;
        return;
      }

      try {
        await this.$http.delete(`/api/campaigns/${this.campaignId}`);

        this.closeModal(true);
      } catch (e) {
        if (e.response && e.response.status == 400) {
          const messages = e.response.data.message;
          const message = messages[0];
          const field = messages[0].split(' ')[0];

          this.error = { errorMessage: message, errorField: field };
        } else {
          this.error = 'Something went wrong';
        }
      } finally {
        this.submitting = false;
      }
    },

    async fetchCampaign() {
      try {
        this.submitting = true;
        const { data = {} } = await this.$http.get(`/api/campaigns/${this.campaignId}`);

        for (const key of Object.keys(errorFieldMap)) {
          let value = data[key] ?? '';

          if (key == 'availableFrom' || key == 'availableUntil') {
            value = moment(value).format('YYYY-MM-DD');
          }

          this.campaign[key] = value;
          this.untouchedCampaign[key] = value;
        }
      } catch (e) {
        this.error.errorMessage = getErrorMessage(e);
      } finally {
        this.submitting = false;
      }
    },

    async fetchProducts() {
      if (this.allProducts.length) {
        return;
      }

      const { data = [] } = await this.$http.get(`/api/products`);

      this.allProducts = data
        .sort((a, b) => {
          if (a.product_group_id < b.product_group_id) {
            return -1;
          }
          if (a.product_group_id > b.product_group_id) {
            return 1;
          }
          if (a.id < b.id) {
            return -1;
          }
          return 1;
        })
        .map((product) => {
          return { id: product.id, name: product.name };
        });
    },

    validateForm() {
      const {
        // type,
        name,
        displayName,
        description,
        availableFrom,
        availableUntil,
        discountPercent,
        appliesForPeriods,
        // active,
        productIds,
      } = this.campaign;

      if (!name) {
        return {
          errorMessage: 'Field "Name" is required',
          errorField: errorFieldMap.name,
        };
      }

      if (!displayName) {
        return {
          errorMessage: 'Field "Display name" is required',
          errorField: errorFieldMap.displayName,
        };
      }

      if (!description) {
        return {
          errorMessage: 'Field "Description" is required',
          errorField: errorFieldMap.description,
        };
      }

      const now = moment().startOf('day');

      if (moment(availableFrom).isAfter(availableUntil)) {
        return {
          errorMessage: 'Available from date should be before available until date',
          errorField: errorFieldMap.availableFrom,
        };
      }

      if (!this.campaign.hasStarted && moment(availableFrom).isBefore(now)) {
        return {
          errorMessage: 'Available from date should be in the future',
          errorField: errorFieldMap.availableFrom,
        };
      }

      if (moment(availableUntil).isBefore(now)) {
        return {
          errorMessage: 'Available until date should be in the future',
          errorField: errorFieldMap.availableUntil,
        };
      }

      if (!discountPercent) {
        return {
          errorMessage: 'Field "Discount percent" is required',
          errorField: errorFieldMap.discountPercent,
        };
      }

      if (isNaN(discountPercent) || discountPercent < 1 || discountPercent > 100) {
        return {
          errorMessage: 'Discount percent should be a number between 1 and 100',
          errorField: errorFieldMap.discountPercent,
        };
      }

      if (!appliesForPeriods) {
        return {
          errorMessage: 'Field "Applies for periods" is required',
          errorField: errorFieldMap.appliesForPeriods,
        };
      }

      if (isNaN(appliesForPeriods) || appliesForPeriods < 1 || appliesForPeriods > 12) {
        return {
          errorMessage: 'Applies for periods should be a number between 1 and 12',
          errorField: errorFieldMap.appliesForPeriods,
        };
      }

      if (productIds.length === 0) {
        return {
          errorMessage: 'At least one product should be selected',
          errorField: errorFieldMap.productIds,
        };
      }

      return false;
    },

    async onCreate() {
      const error = this.validateForm();

      if (error) {
        this.error = error;
        return;
      }

      const changedFields = {};
      for (const key of Object.keys(this.campaign)) {
        let value = this.campaign[key];

        if (key === 'availableFrom' || key === 'availableUntil') {
          value = moment(value).toISOString();
        } else if (key === 'appliesForPeriods' || key === 'discountPercent') {
          value = Number(value);
        }

        changedFields[key] = value;
      }

      try {
        await this.$http.post(`/api/campaigns`, {
          ...changedFields,
        });

        this.closeModal(true);
      } catch (e) {
        if (e.response && e.response.status == 400) {
          const messages = e.response.data.message;
          const message = messages[0];
          const field = messages[0].split(' ')[0];

          this.error = { errorMessage: message, errorField: field };
        } else {
          this.error = 'Something went wrong';
        }
      } finally {
        this.submitting = false;
      }
    },

    async onUpdate() {
      const changedFields = {};
      for (const key of Object.keys(this.campaign)) {
        if (this.campaign[key] !== this.untouchedCampaign[key]) {
          let value = this.campaign[key];

          if (key === 'availableFrom' || key === 'availableUntil') {
            value = moment(value).toISOString();
          } else if (key === 'appliesForPeriods' || key === 'discountPercent') {
            value = Number(value);
          }

          changedFields[key] = value;
        }
      }

      try {
        const error = this.validateForm();

        if (error) {
          this.error = error;
          return;
        }

        if (Object.keys(changedFields).length == 0) {
          this.closeModal();
          this.submitting = false;
          return;
        }

        this.submitting = true;

        await this.$http.patch(`/api/campaigns/${this.campaignId}`, {
          ...changedFields,
        });

        this.closeModal(true);
        this.error = { errorMessage: '', errorField: '' };
      } catch (e) {
        if (e.response && e.response.status == 400) {
          const messages = e.response.data.message;
          const message = messages[0];
          const field = messages[0].split(' ')[0];

          this.error = { errorMessage: message, errorField: field };
        } else {
          this.error = 'Something went wrong';
        }
      } finally {
        this.submitting = false;
      }
    },

    async promoteCampaign() {
      if (this.campaign.type !== 'signup') {
        this.error = {
          errorMessage: this.$t('@campaigns/promoteError'),
          errorField: errorFieldMap.type,
        };
        return;
      }

      this.submitting = true;

      try {
        await this.$http.post(`/api/campaigns/${this.campaignId}/promote`, {});

        this.closeModal(true);
      } catch (e) {
        if (e.response && e.response.status == 400) {
          const messages = e.response.data.message;
          if (messages.some((m) => m.includes('overlap'))) {
            const result = confirm(this.$t('@campaigns/promoteOverlapConfirm'));

            if (result) {
              await this.$http.post(`/api/campaigns/${this.campaignId}/promote?override=true`, {});
              this.closeModal(true);
            }
          } else {
            const message = messages[0];
            const field = messages[0].split(' ')[0];

            this.error = { errorMessage: message, errorField: field };
          }
        } else {
          this.error = 'Something went wrong';
        }
      } finally {
        this.submitting = false;
      }
    },

    async unpromoteCampaign() {
      if (this.campaign.type !== 'signup') {
        this.error = {
          errorMessage: this.$t('@campaigns/unpromoteError'),
          errorField: errorFieldMap.type,
        };
        return;
      }

      this.submitting = true;

      try {
        await this.$http.post(`/api/campaigns/${this.campaignId}/unpromote`, {});

        this.closeModal(true);
      } catch (e) {
        if (e.response && e.response.status == 400) {
          const messages = e.response.data.message;

          const message = messages[0];
          const field = messages[0].split(' ')[0];

          this.error = { errorMessage: message, errorField: field };
        } else {
          this.error = 'Something went wrong';
        }
      } finally {
        this.submitting = false;
      }
    },

    async onSubmit() {
      if (!this.campaignId) {
        this.onCreate();
      } else {
        this.onUpdate();
      }
    },
  },
};
</script>
